/**
 * Applies the admin-configured `site_meta` document to the live DOM:
 *   - <html lang>
 *   - <title>
 *   - <meta name="description">, "keywords", "theme-color", "robots"
 *   - <link rel="icon">, "apple-touch-icon"
 *   - OpenGraph + Twitter meta tags
 *   - <link rel="canonical">
 *
 * Idempotent: each tag is identified by a stable `data-sm` attribute so
 * subsequent fetches/refreshes update the same tags instead of duplicating.
 * Safe to call multiple times.
 */

const API_PREFIX = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function ensureMeta(name, attr = "name") {
  let el = document.head.querySelector(`meta[${attr}="${name}"][data-sm="1"]`);
  if (el) return el;
  // Reuse an existing tag set by index.html if present (avoid duplicates).
  el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (el) { el.setAttribute("data-sm", "1"); return el; }
  el = document.createElement("meta");
  el.setAttribute(attr, name);
  el.setAttribute("data-sm", "1");
  document.head.appendChild(el);
  return el;
}

function setMeta(name, content, attr = "name") {
  if (!content) return;
  ensureMeta(name, attr).setAttribute("content", content);
}

function ensureLink(rel) {
  let el = document.head.querySelector(`link[rel="${rel}"][data-sm="1"]`);
  if (el) return el;
  el = document.head.querySelector(`link[rel="${rel}"]`);
  if (el) { el.setAttribute("data-sm", "1"); return el; }
  el = document.createElement("link");
  el.setAttribute("rel", rel);
  el.setAttribute("data-sm", "1");
  document.head.appendChild(el);
  return el;
}

export function applySiteMeta(meta) {
  if (!meta || typeof meta !== "object") return;

  if (meta.language) document.documentElement.setAttribute("lang", meta.language);
  if (meta.page_title) document.title = meta.page_title;

  setMeta("description", meta.meta_description);
  setMeta("keywords", meta.meta_keywords);
  setMeta("theme-color", meta.theme_color);
  setMeta("robots", meta.robots);

  // OpenGraph (property attribute, not name)
  setMeta("og:title", meta.og_title || meta.page_title, "property");
  setMeta("og:description", meta.og_description || meta.meta_description, "property");
  setMeta("og:image", meta.og_image_url, "property");
  setMeta("og:url", meta.og_url, "property");
  setMeta("og:type", meta.og_type, "property");
  setMeta("og:site_name", meta.og_site_name || meta.site_name, "property");

  // Twitter card
  setMeta("twitter:card", meta.twitter_card);
  setMeta("twitter:site", meta.twitter_site);
  setMeta("twitter:title", meta.twitter_title || meta.og_title || meta.page_title);
  setMeta("twitter:description", meta.twitter_description || meta.og_description || meta.meta_description);
  setMeta("twitter:image", meta.twitter_image_url || meta.og_image_url);

  if (meta.canonical_url) {
    ensureLink("canonical").setAttribute("href", meta.canonical_url);
  }

  // Favicon — prefer the admin upload, fall back to the bundled iceberg logo.
  if (meta.favicon_url) {
    const full = meta.favicon_url.startsWith("http")
      ? meta.favicon_url
      : `${process.env.REACT_APP_BACKEND_URL || ""}${meta.favicon_url}`;
    ensureLink("icon").setAttribute("href", full);
    ensureLink("apple-touch-icon").setAttribute("href", full);
  }
}

export async function bootstrapSiteMeta() {
  try {
    const res = await fetch(`${API_PREFIX}/site-meta`, { credentials: "include" });
    if (!res.ok) return;
    const meta = await res.json();
    applySiteMeta(meta);
  } catch (e) {
    // Public endpoint — should never fail in production, but if it does
    // the user just gets the default index.html title which is fine.
  }
}
